import { AxiosInstance } from "axios";
import { createApiClient } from "../api/createApiClient";
import {
  FintracJobRunsResponse,
  FintracListParams,
  FintracReportsResponse,
  FintracTransactionsResponse,
  FintracWalletParty,
  UpdateFintracReportStatusRequest,
  UpdateFintracReportStatusResponse,
} from "../models/Fintrac/FintracModel";

const partyText = (value: unknown) =>
  value === null || value === undefined ? "" : String(value);

const toWalletParty = (value: unknown): FintracWalletParty | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  return {
    id: partyText(row.id),
    externalUserId: partyText(row.externalUserId),
    type: partyText(row.type),
    firstName: partyText(row.firstName),
    lastName: partyText(row.lastName),
    fullName: partyText(row.fullName),
    dateOfBirth: partyText(row.dateOfBirth),
    placeOfBirth: partyText(row.placeOfBirth),
    residenceCountry: partyText(row.residenceCountry),
    addressCountry: partyText(row.addressCountry),
    addressTown: partyText(row.addressTown),
    addressPostCode: partyText(row.addressPostCode),
    addressStreet: partyText(row.addressStreet),
    addressBuildingNumber: partyText(row.addressBuildingNumber),
    relationship: partyText(row.relationship),
    declaredByUserId: partyText(row.declaredByUserId),
    createdAt: partyText(row.createdAt),
    updatedAt: partyText(row.updatedAt),
  };
};

const walletPartiesFromPayload = (payload: unknown): FintracWalletParty[] => {
  const source = Array.isArray(payload)
    ? payload
    : payload &&
        typeof payload === "object" &&
        "data" in payload &&
        (payload as { data?: unknown }).data !== undefined
      ? (payload as { data?: unknown }).data
      : payload;
  const rows = Array.isArray(source) ? source : source ? [source] : [];
  return rows
    .map(toWalletParty)
    .filter((party): party is FintracWalletParty => party !== null);
};

const compactParams = (params: FintracListParams = {}) => {
  const query: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query[key] = value;
  });
  return query;
};

class FintracService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient();
  }

  public async listJobRuns(
    params: FintracListParams = {},
  ): Promise<FintracJobRunsResponse> {
    const response = await this.api.get<FintracJobRunsResponse>(
      "/adminPanel/fintrac/job-runs",
      { params: compactParams(params) },
    );
    return response.data;
  }

  public async listReports(
    jobRunId: number,
    params: FintracListParams = {},
  ): Promise<FintracReportsResponse> {
    const response = await this.api.get<FintracReportsResponse>(
      `/adminPanel/fintrac/job-runs/${encodeURIComponent(String(jobRunId))}/reports`,
      { params: compactParams(params) },
    );
    return response.data;
  }

  public async listTransactions(
    reportId: number,
    params: FintracListParams = {},
  ): Promise<FintracTransactionsResponse> {
    const response = await this.api.get<FintracTransactionsResponse>(
      `/adminPanel/fintrac/reports/${encodeURIComponent(String(reportId))}/transactions`,
      { params: compactParams(params) },
    );
    return response.data;
  }

  public async getCounterpartyByWalletAddress(
    walletAddress: string,
  ): Promise<FintracWalletParty[]> {
    const response = await this.api.get<unknown>(
      "/adminPanel/fintrac/counterparty-data-from-wallet-address",
      { params: { walletAddress } },
    );
    return walletPartiesFromPayload(response.data);
  }

  public async updateReportStatus(
    reportId: number,
    body: UpdateFintracReportStatusRequest,
  ): Promise<UpdateFintracReportStatusResponse> {
    const response = await this.api.put<UpdateFintracReportStatusResponse>(
      `/adminPanel/fintrac/reports/${encodeURIComponent(String(reportId))}/status`,
      body,
    );
    return response.data;
  }
}

export const fintracService = new FintracService();
